import {
  type PmBlockquote,
  type PmBulletList,
  type PmCodeBlock,
  type PmDoc,
  type PmInline,
  type PmListItem,
  PmMarkType,
  type PmNode,
  PmNodeType,
  type PmOrderedList,
  type PmParagraph,
  type PmText,
} from "@storytell/types/richText";
import type { VariableKind } from "@storytell/types/richText";
import { For, type JSX, Match, Switch, createContext, useContext } from "solid-js";
import { Dynamic } from "solid-js/web";
import { promptClasses } from "./promptClasses";

const StaticRendererContext = createContext<{
  variableV1Renderer: (props: {
    type: VariableKind;
    notInPromptBar?: boolean;
    id: string;
    selected?: boolean;
    text?: string;
    onDelete: () => void;
    isEditable?: boolean;
  }) => JSX.Element;
}>();

export const PromptStaticRenderer = (props: {
  doc: PmDoc;
  variableV1Renderer: (props: {
    type: VariableKind;
    notInPromptBar?: boolean;
    id: string;
    selected?: boolean;
    text?: string;
    onDelete: () => void;
    isEditable?: boolean;
  }) => JSX.Element;
}) => {
  return (
    <StaticRendererContext.Provider value={props}>
      <div class={promptClasses.wrapper}>
        <For each={props.doc.content}>{(node) => <NodeRenderer node={node} />}</For>
      </div>
    </StaticRendererContext.Provider>
  );
};

const NodeRenderer = (props: { node: PmNode }) => {
  return (
    <Switch>
      <Match when={props.node.type === PmNodeType.Paragraph && props.node}>
        {(node) => <ParagraphRenderer node={node()} />}
      </Match>
      <Match when={props.node.type === PmNodeType.BulletList && props.node}>
        {(node) => <BulletListRenderer node={node()} />}
      </Match>
      <Match when={props.node.type === PmNodeType.OrderedList && props.node}>
        {(node) => <OrderedListRenderer node={node()} />}
      </Match>
      <Match when={props.node.type === PmNodeType.ListItem && props.node}>
        {(node) => <ListItemRenderer node={node()} />}
      </Match>
      <Match when={props.node.type === PmNodeType.Blockquote && props.node}>
        {(node) => <BlockquoteRenderer node={node()} />}
      </Match>
      <Match when={props.node.type === PmNodeType.CodeBlock && props.node}>
        {(node) => <CodeBlockRenderer node={node()} />}
      </Match>
    </Switch>
  );
};

const TextRenderer = (props: { node: PmText }) => {
  const classes = () => {
    const classes: string[] = [];
    for (const mark of props.node.marks || []) {
      switch (mark.type) {
        case PmMarkType.Bold:
          classes.push(promptClasses.bold);
          break;
        case PmMarkType.Italic:
          classes.push(promptClasses.italic);
          break;
        case PmMarkType.Code:
          classes.push(promptClasses.code);
          break;
        // TODO: @andi Strike and Link
        // case PmMarkType.Link:
        //   classes.push(promptClasses.link);
        //   break;
        // case PmMarkType.Strike:
        //   classes.push(promptClasses.strike);
        //   break;
      }
    }
    return classes.join(" ");
  };
  return <span class={classes()}>{props.node.text}</span>;
};

const InlineNodeRenderer = (props: { node: PmInline }) => {
  const ctx = useContext(StaticRendererContext);
  return (
    <Switch>
      <Match when={props.node.type === PmNodeType.Text && props.node}>{(node) => <TextRenderer node={node()} />}</Match>
      <Match when={props.node.type === PmNodeType.VariableV1 && props.node}>
        {(node) => (
          <Dynamic
            component={ctx?.variableV1Renderer}
            id={node().attrs.id}
            type={node().attrs.type}
            onDelete={() => {}}
            notInPromptBar={true}
            isEditable={false}
            selected={false}
            text={node().attrs.text}
          />
        )}
      </Match>
    </Switch>
  );
};
const ParagraphRenderer = (props: { node: PmParagraph }) => {
  return (
    <p>
      <For each={props.node.content}>{(node) => <InlineNodeRenderer node={node} />}</For>
    </p>
  );
};

const ListItemRenderer = (props: { node: PmListItem }) => {
  return (
    <li>
      <For each={props.node.content}>{(node) => <NodeRenderer node={node} />}</For>
    </li>
  );
};
const BulletListRenderer = (props: { node: PmBulletList }) => {
  return (
    <ul class={promptClasses.bulletList}>
      <For each={props.node.content}>{(node) => <ListItemRenderer node={node} />}</For>
    </ul>
  );
};
const OrderedListRenderer = (props: { node: PmOrderedList }) => {
  return (
    <ol class={promptClasses.orderedList}>
      <For each={props.node.content}>{(node) => <ListItemRenderer node={node} />}</For>
    </ol>
  );
};

const BlockquoteRenderer = (props: { node: PmBlockquote }) => {
  return (
    <blockquote class={promptClasses.blockquote}>
      <For each={props.node.content}>{(node) => <NodeRenderer node={node} />}</For>
    </blockquote>
  );
};
const CodeBlockRenderer = (props: { node: PmCodeBlock }) => {
  return (
    <pre class={promptClasses.codeBlock}>
      <code>
        <For each={props.node.content}>{(node) => <TextRenderer node={node} />}</For>
      </code>
    </pre>
  );
};
